import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';

import type { LoadError } from '../api/client.ts';
import { setPageTitle } from '../app/page-title.ts';
import { Link } from '../app/router.tsx';
import type { MenuScreen } from '../platform/analytics.ts';
import '../styles/app-states.css';
import { AppMenu } from './AppMenu.tsx';
import { BackPill, type BackTo } from './BackPill.tsx';
import { DartMark } from './DartMark.tsx';
import { Nib } from './Nib.tsx';

/** What a failed load says, in the brand voice. Never the raw error (dev builds add it). */
export function errorCopy(kind: LoadError['kind'], what = 'today’s Sparrow', plural = false): { line: string; sub: string } {
	const What = what.charAt(0).toUpperCase() + what.slice(1);
	const isnt = plural ? 'aren’t' : 'isn’t';
	switch (kind) {
		case 'network':
			return { line: `Couldn’t load ${what}.`, sub: 'Check your connection.' };
		case 'not-found':
			return { line: `${What} ${isnt} out yet.`, sub: 'Check back in a little while.' };
		case 'invalid':
			return { line: `${What} didn’t load properly.`, sub: 'Try again in a moment.' };
	}
}

// --------------------------------------------------------------------------- loading

/**
 * A wait that's quick shows nothing at all: `children` (a skeleton, the lockup) appear only
 * once `delay` ms have passed (--skeleton-delay), so a fast load never flashes a placeholder
 * (UX-015). The screen-reader `label` is there from the start.
 */
export function Pending({ delay = 300, label, children }: { delay?: number; label?: string; children: ReactNode }) {
	const [shown, setShown] = useState(delay <= 0);
	useEffect(() => {
		if (delay <= 0) return;
		const t = window.setTimeout(() => setShown(true), delay);
		return () => window.clearTimeout(t);
	}, [delay]);
	return (
		<>
			{label && (
				<p className="sr-only" role="status">
					{label}
				</p>
			)}
			{shown && children}
		</>
	);
}

/**
 * A quiet placeholder block while something loads (app-states.css `.skel`): give it a size
 * with `className` or `style`. Decorative, so hidden from screen readers; say "Loading" once,
 * with `Pending`'s label.
 */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
	return <span className={className ? `skel ${className}` : 'skel'} style={style} aria-hidden="true" />;
}

/** A list's placeholder: `count` rows, each a title line and a shorter meta line. */
export function SkeletonRows({ count = 4 }: { count?: number }) {
	return (
		<div className="skel-rows" aria-hidden="true">
			{Array.from({ length: count }, (_, i) => (
				<div className="skel-row" key={i}>
					<span className="skel skel-line" />
					<span className="skel skel-line skel-short" />
				</div>
			))}
		</div>
	);
}

/** The way home over a loading state, in the masthead's corner (not on Today's own). */
function StateBack({ to }: { to: BackTo | undefined }) {
	if (!to) return null;
	return (
		<header className="state-top">
			<BackPill to={to} />
		</header>
	);
}

/**
 * Cold boot: the lockup alone, its Dart pressing into the paper; "‹ Today" when it isn't Today
 * loading. It appears only if the wait is noticeable (`Pending`). A page that's already up
 * shows its own shell with a skeleton in the content slot instead.
 */
export function LoadingState({ label = 'Loading today’s Sparrow', back }: { label?: string; back?: BackTo | undefined }) {
	return (
		<div className="app app-state">
			<StateBack to={back} />
			<main className="state state-loading" aria-busy="true">
				<Pending label={label}>
					<h1 className="state-lockup">
						<DartMark className="state-mark" />
						<span className="state-wm">sparrow</span>
					</h1>
				</Pending>
			</main>
		</div>
	);
}

// --------------------------------------------------------------------------- the state page

/** The wordmark as the way home, where there's no "‹ Today" (a first visit from a link). */
function HomeMark() {
	return (
		<Link to="/" how="up" className="brand brand-home" aria-label="Sparrow, today’s puzzles">
			<DartMark className="brand-mark" />
			<span className="wm" aria-hidden="true">
				sparrow
			</span>
		</Link>
	);
}

/**
 * Every "something's wrong" page has one frame (UX-016): the masthead row (the back pill when
 * there's somewhere to go back to, otherwise the wordmark, which goes to Today), always its
 * rule, always ≡; then the page's words (`StateBody`) on the left edge.
 */
export function StatePage({ back, screen = 'today', children, testId }: { back?: BackTo | undefined; screen?: MenuScreen; children: ReactNode; testId?: string }) {
	return (
		<div className="app app-state landing" data-testid={testId}>
			<header className="page-mast state-mast">
				{back ? <BackPill to={back} /> : <HomeMark />}
				<AppMenu screen={screen} install={screen === 'game' ? undefined : {}} />
			</header>
			<main className="state state-error">{children}</main>
		</div>
	);
}

/**
 * A state's words: ruffled Nib, one calm line, what to do, then the way on, at fixed
 * spacing. `heading` is the line's level: 1 on a page of its own, 2 inside a page that has
 * its own title.
 */
export function StateBody({
	line,
	sub,
	heading = 1,
	alert = false,
	children,
}: {
	line: ReactNode;
	sub?: ReactNode;
	heading?: 1 | 2;
	/** Announce the line at once (a failure the reader didn't ask for). */
	alert?: boolean;
	/** The actions (a pill, then a quiet link) and anything else under the sub. */
	children?: ReactNode;
}) {
	const H = heading === 1 ? 'h1' : 'h2';
	return (
		<div className="state-body">
			<Nib pose="ruffled" className="state-nib" />
			<H className="state-line">{alert ? <span role="alert">{line}</span> : line}</H>
			{sub && <p className="state-sub">{sub}</p>}
			{children}
		</div>
	);
}

interface ErrorStateProps {
	error: LoadError;
	what?: string;
	/** `what` names more than one thing ("past Sparrows"): "…aren’t out yet". */
	plural?: boolean;
	onRetry: () => void;
	/** Dev builds: the raw message and an escape hatch. */
	dev?: { message: boolean; back?: { label: string; onClick: () => void } | undefined } | undefined;
	/** A second, quiet way out (e.g. back to Today). */
	link?: { label: string; onClick: () => void } | undefined;
	/** "‹ Today" / "‹ Play" in the corner (every screen without the tab bar). */
	back?: BackTo | undefined;
	/**
	 * A tab's own error (Today, Nest): the error alone, for the page's content slot, so the
	 * brand row, ≡ and the tab bar stay, and Stats, Play and Settings are still a tap away.
	 */
	inPage?: boolean;
	/** A game is in play here (Today, a puzzle, Play): say the progress is safe. */
	game?: boolean;
	/** Which screen ≡ reports opening from (the state page's menu). */
	screen?: MenuScreen;
}

/**
 * Error: the state page (or, `inPage`, the error alone inside a tab): ruffled Nib, one calm
 * line and an ink pill with a vermilion lip, all on the page's left edge. A failure is part
 * of the app, not a splash. The tab's title says what went wrong.
 */
export function ErrorState({ error, what, plural = false, onRetry, dev, link, back, inPage = false, game = false, screen }: ErrorStateProps) {
	const copy = errorCopy(error.kind, what, plural);
	// The tab (and a screen reader's first words) say what went wrong, not just "Sparrow".
	useEffect(() => {
		setPageTitle(copy.line);
	}, [copy.line]);
	const body = (
		<StateBody line={copy.line} sub={game ? `${copy.sub} Your progress is saved on this device.` : copy.sub} heading={inPage ? 2 : 1} alert>
			{dev?.message && <p className="state-dev">{error.message}</p>}
			<div className="state-actions">
				{/* Not out yet: trying again now won't help, so the way out leads. */}
				{error.kind === 'not-found' && link ? (
					<>
						<button type="button" className="btn lg primary fit" onClick={link.onClick}>
							{link.label}
						</button>
						<button type="button" className="tlink" onClick={onRetry}>
							Try again
						</button>
					</>
				) : (
					<>
						<button type="button" className="btn lg primary fit" onClick={onRetry}>
							Try again
						</button>
						{link && (
							<button type="button" className="tlink" onClick={link.onClick}>
								{link.label}
							</button>
						)}
					</>
				)}
				{dev?.back && (
					<button type="button" className="tlink" onClick={dev.back.onClick}>
						{dev.back.label}
					</button>
				)}
			</div>
		</StateBody>
	);
	if (inPage) return <section className="state-inpage">{body}</section>;
	return (
		<StatePage back={back} screen={screen}>
			{body}
		</StatePage>
	);
}
